import { useEffect, useMemo, useState } from "react";
import { matchPath, Outlet, useLocation } from "react-router-dom";
import type { JournalExplorerContext } from "@/components/JournalExplorer";
import { JournalExplorer, JournalExplorerDrawer } from "@/components/JournalExplorer";
import MobileHeader from "@/components/MobileHeader";
import { WatchlistDrawer } from "@/components/WatchlistDrawer";
import { userServiceClient } from "@/connect";
import useCurrentUser from "@/hooks/useCurrentUser";
import { useFilteredMemoStats } from "@/hooks/useFilteredJournalStats";
import useMediaQuery from "@/hooks/useMediaQuery";
import { useSidebarCollapse } from "@/hooks/useSidebarCollapse";
import { cn } from "@/lib/utils";
import { Routes } from "@/router";

const MainLayout = () => {
  const md = useMediaQuery("md");
  const lg = useMediaQuery("lg");
  const location = useLocation();
  const currentUser = useCurrentUser();
  const [profileUserName, setProfileUserName] = useState<string | undefined>();
  const { isCollapsed: isNavCollapsed } = useSidebarCollapse();

  // Determine context based on current route
  const context: JournalExplorerContext = useMemo(() => {
    if (location.pathname === Routes.ROOT) return "home";
    if (matchPath("/archived", location.pathname)) return "archived";
    if (matchPath("/u/:username", location.pathname)) return "profile";
    if (matchPath("/checklist-templates", location.pathname)) return null; // No explorer for templates
    if (matchPath("/tickers", location.pathname)) return null; // No explorer for tickers
    if (matchPath("/actions", location.pathname)) return null; // No explorer for actions
    return "home"; // fallback
  }, [location.pathname]);

  // Extract username from URL for profile context
  useEffect(() => {
    const match = matchPath("/u/:username", location.pathname);
    if (match && context === "profile") {
      const username = match.params.username;
      if (username) {
        // Fetch or get user to obtain user name (e.g., "users/123")
        // Note: User stats will be fetched by useFilteredMemoStats
        userServiceClient
          .getUser({ name: `users/${username}` })
          .then((user) => {
            setProfileUserName(user.name);
          })
          .catch((error) => {
            console.error("Failed to fetch profile user:", error);
            setProfileUserName(undefined);
          });
      }
    } else {
      setProfileUserName(undefined);
    }
  }, [location.pathname, context]);

  // Determine which user name to use for stats
  // - home: current user (uses backend user stats for normal memos)
  // - profile: viewed user (uses backend user stats for normal memos)
  // - archived: undefined (compute from cached archived memos, since user stats only includes normal memos)
  const statsUserName = useMemo(() => {
    if (context === "home") {
      return currentUser?.name;
    } else if (context === "profile") {
      return profileUserName;
    }
    return undefined; // archived context computes from cache
  }, [context, currentUser, profileUserName]);

  // Fetch stats from memo store cache (populated by PagedMemoList)
  // For user-scoped contexts, use backend user stats for tags (unaffected by filters)
  const { tags, tickers } = useFilteredMemoStats({ userName: statsUserName });

  return (
    <section className="@container w-full min-h-full flex flex-col md:flex-row relative">
      {/* Animated background gradient - subtle ambient effect */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden -z-10">
        {/* Top-right gradient bloom */}
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/3 via-transparent to-amber-500/3 rounded-full blur-3xl" />

        {/* Bottom-left gradient bloom */}
        <div className="absolute -bottom-60 -left-60 w-[32rem] h-[32rem] bg-gradient-to-tr from-amber-500/3 via-transparent to-blue-500/3 rounded-full blur-3xl" />

        {/* Subtle noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.02] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZmlsdGVyIGlkPSJub2lzZSI+PGZlVHVyYnVsZW5jZSB0eXBlPSJmcmFjdGFsTm9pc2UiIGJhc2VGcmVxdWVuY3k9IjAuNjUiIG51bU9jdGF2ZXM9IjMiIHN0aXRjaFRpbGVzPSJzdGl0Y2giLz48L2ZpbHRlcj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWx0ZXI9InVybCgjbm9pc2UpIiBvcGFjaXR5PSIwLjEiLz48L3N2Zz4=')]" />
      </div>

      {!md && (
        <MobileHeader>
          {context !== null && <JournalExplorerDrawer context={context} tagCount={tags} tickerCount={tickers} />}
          {location.pathname === Routes.TICKERS && <WatchlistDrawer />}
        </MobileHeader>
      )}
      {md && context !== null && (
        <div
          className={cn(
            "fixed top-0 shrink-0 h-svh transition-all duration-300",
            // Enhanced sidebar with glass effect
            "border-r border-border/50 backdrop-blur-sm bg-background/80",
            // Dynamic position based on left sidebar state
            isNavCollapsed ? "left-16" : "left-56",
            // Dynamic width based on screen size
            lg ? "w-72" : "w-56",
          )}
        >
          <JournalExplorer className={cn("px-3 py-6")} context={context} tagCount={tags} tickerCount={tickers} />
        </div>
      )}
      <div
        className={cn(
          "flex-1 min-h-full transition-all duration-300 flex justify-center",
          // Mobile: no padding (sidebar hidden in drawer)
          !md && "pl-0",
          // Desktop: dynamic padding based on left nav state and context
          md &&
            (context === null
              ? "pl-0" // No explorer, no padding needed
              : isNavCollapsed
                ? lg
                  ? "pl-88"
                  : "pl-72" // 64px nav + 288px/224px explorer
                : lg
                  ? "pl-[38rem]"
                  : "pl-[34rem]"), // 224px nav + 288px/224px explorer
        )}
      >
        {/* Main content wrapper */}
        {/* When context is null (TickerList), don't constrain width - let the page control its own layout */}
        {/* When context exists (Home/Profile/Archived), constrain width for centered content */}
        <div
          className={cn(
            "w-full transition-all duration-300",
            context === null ? "" : lg ? "max-w-5xl" : "max-w-3xl",
            context === null ? "" : "px-4 sm:px-6 md:pt-6 pb-8",
          )}
        >
          <Outlet />
        </div>
      </div>
    </section>
  );
};

export default MainLayout;
